import { useCallback, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import {
  editActions,
  editSelectors,
} from 'store/reducers/releaseReducers/releaseComponentReducer/editSlice';
import { registrationActions } from 'store/reducers/releaseReducers/releaseComponentReducer/registrationSlice';
import { ReleaseComponent, ReleaseComponentApi } from 'types/releaseComponent';
import { useListStore } from './useListStore';
import { EditHookReturnType } from '../types';

/**
 * コンポーネント登録 store の実装をコンポーネントから隠すための中間層
 */
export const useEditStore = (): EditHookReturnType => {
  const dispatch = useAppDispatch();
  const { fetchReleaseComponents } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseComponent>({
    mode: 'all',
  });

  const { getValues, reset, handleSubmit, control, watch } = form;

  const { fields, append, remove } = useFieldArray({
    name: 'targetKeys',
    control,
  });
  const watchFieldArray = watch('targetKeys');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const onAppend = () => {
    append({ value: '' });
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  const currentModalStep = useAppSelector(editSelectors.currentModalStep);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    dispatch(editActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseComponents();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ReleaseComponent) => {
      dispatch(loaderActions.setLoading(true));
      // api送信用にデータ修正
      const targetKeys: string[] = [];
      values.targetKeys.forEach((targetKey) => {
        targetKeys.push(targetKey.value);
      });
      const ApiValues: ReleaseComponentApi = {
        id: values.id,
        componentName: values.componentName,
        repositoryUrl: values.repositoryUrl,
        manifestName: values.manifestName,
        targetKeys,
      };
      const componentId = values.id ? values.id : 0;
      const result = await releaseApi.editComponent(componentId, ApiValues);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `コンポーネントの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'コンポーネントの登録に失敗しました。',
      });
    },
  );

  const onOpenModalDelete = useCallback(() => {
    dispatch(editActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const selectedReleaseComponent = useAppSelector(
    editSelectors.selectedReleaseComponent,
  );

  useEffect(() => {
    if (selectedReleaseComponent) {
      reset(selectedReleaseComponent);
    }
  }, [selectedReleaseComponent, reset]);

  return {
    mode: '更新',
    isOpenModal: !!selectedReleaseComponent,
    currentModalStep,
    onOpenModal: () => null,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete,
    form,
    values: getValues(),
    onAppend,
    onRemove,
    controlledFields,
  };
};
